import { frontendURL } from '../../../helper/URLHelper';

import ProjectList from './projects/ProjectList.vue';
import ProjectForm from './projects/ProjectForm.vue';
import AiEmployeeSettings from './projects/AiEmployeeSettings.vue';
import ProjectDocuments from './projects/ProjectDocuments.vue';
import ProjectInventory from './projects/ProjectInventory.vue';
import ProjectFloorPlans from './projects/ProjectFloorPlans.vue';
import ProjectKnowledge from './projects/ProjectKnowledge.vue';
import SalesProjectList from './salesProjects/SalesProjectList.vue';
import SalesProjectShow from './salesProjects/SalesProjectShow.vue';
import KanbanPage from './kanban/KanbanPage.vue';
import CalendarPage from './calendar/CalendarPage.vue';
import AllLeadsPage from './AllLeadsPage.vue';
import VerifiedLeadsPage from './VerifiedLeadsPage.vue';
import NexceoLeadFormPage from './NexceoLeadFormPage.vue';
import LeadWorkspace from './leadWorkspace/LeadWorkspace.vue';
import AiAnalyticsPage from './AiAnalyticsPage.vue';
import AiApprovalsPage from './AiApprovalsPage.vue';
import AiWorkforcePage from './AiWorkforcePage.vue';
import WorkforceTasksPage from './WorkforceTasksPage.vue';
import WorkforceAnalyticsPage from './WorkforceAnalyticsPage.vue';
import AiCoreHealthPage from './AiCoreHealthPage.vue';
import RuntimeInspectorPage from './RuntimeInspectorPage.vue';
import CompanyAiPage from './CompanyAiPage.vue';
import NexceoSettings from './NexceoSettings.vue';
import NexceoReportsPage from './NexceoReportsPage.vue';
import StaffAIPage from './StaffAIPage.vue';
import LeadGenerationPage from './LeadGenerationPage.vue';

const meta = {
  permissions: ['administrator', 'agent'],
};

const adminMeta = {
  permissions: ['administrator'],
};

export const routes = [
  {
    path: frontendURL('accounts/:accountId/nexceo/kanban'),
    name: 'nexceo_kanban',
    component: KanbanPage,
    meta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/calendar'),
    name: 'nexceo_calendar',
    component: CalendarPage,
    meta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/all-leads'),
    name: 'nexceo_all_leads',
    component: AllLeadsPage,
    meta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/verified-leads'),
    name: 'nexceo_verified_leads',
    component: VerifiedLeadsPage,
    meta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/leads/new'),
    name: 'nexceo_leads_new',
    component: NexceoLeadFormPage,
    meta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/leads/:id/edit'),
    name: 'nexceo_leads_edit',
    component: NexceoLeadFormPage,
    meta,
  },
  // Lead Workspace — full-page counterpart to the existing LeadDetailsDrawer,
  // reached from the redesigned "My Work" (Verified Leads) list. Kanban and
  // All Leads keep using the drawer unchanged.
  {
    path: frontendURL('accounts/:accountId/nexceo/leads/:id/workspace/:tab?'),
    name: 'nexceo_lead_workspace',
    component: LeadWorkspace,
    meta,
  },
  // Sales Projects — the client-presentation experience (Overview/Documents/
  // Inventory/Media/Maps/Immersive), distinct from the admin control center
  // at Settings > NEXCEO Projects (nexceo_projects_index et al. above, left
  // untouched). Same account-scoped, already-authorized project data — see
  // SalesProjectList.vue / SalesProjectShow.vue.
  {
    path: frontendURL('accounts/:accountId/nexceo/sales/projects'),
    name: 'nexceo_sales_projects_index',
    component: SalesProjectList,
    meta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/sales/projects/:id/:tab?'),
    name: 'nexceo_sales_project_show',
    component: SalesProjectShow,
    meta,
  },
  {
    // M29.5.16A follow-up — backend GET /nexceo/projects now scopes to the
    // current user's own project(s) for non-admins (Nexceo::AccessPolicy
    // #scope_projects) instead of being admin-only outright. ProjectList.vue
    // self-gates its admin-only actions (create/edit/AI settings) via
    // isAdmin, so this only needs to stop blocking the page itself.
    path: frontendURL('accounts/:accountId/nexceo/projects'),
    name: 'nexceo_projects_index',
    component: ProjectList,
    meta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/projects/new'),
    name: 'nexceo_projects_new',
    component: ProjectForm,
    meta: adminMeta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/projects/:id/edit'),
    name: 'nexceo_projects_edit',
    component: ProjectForm,
    meta: adminMeta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/projects/:id/ai'),
    name: 'nexceo_ai_employee_settings',
    component: AiEmployeeSettings,
    meta: adminMeta,
  },
  {
    // M29.5.16A — Agent Navigation Audit: backend (Nexceo::AccessPolicy
    // #can_access_document?/#can_manage_unit_status?) already allows any
    // project member (including a plain agent) to view documents and view/
    // reserve/hold inventory for a project they belong to. This route was
    // still admin-only at the router level, so a backend-permitted agent
    // was blocked from ever reaching the page. Destructive/pricing/import
    // actions stay admin-gated at the component level below — this only
    // restores the view.
    path: frontendURL('accounts/:accountId/nexceo/projects/:id/documents'),
    name: 'nexceo_project_documents',
    component: ProjectDocuments,
    meta,
  },
  {
    // Same fix as nexceo_project_documents above.
    path: frontendURL('accounts/:accountId/nexceo/projects/:id/inventory'),
    name: 'nexceo_project_inventory',
    component: ProjectInventory,
    meta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/projects/:id/floor-plans'),
    name: 'nexceo_project_floor_plans',
    component: ProjectFloorPlans,
    meta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/projects/:id/knowledge'),
    name: 'nexceo_project_knowledge',
    component: ProjectKnowledge,
    meta: adminMeta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/ai-analytics'),
    name: 'nexceo_ai_analytics',
    component: AiAnalyticsPage,
    meta: adminMeta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/ai-approvals'),
    name: 'nexceo_ai_approvals',
    component: AiApprovalsPage,
    meta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/ai-workforce'),
    name: 'nexceo_ai_workforce',
    component: AiWorkforcePage,
    meta: adminMeta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/ai-workforce/tasks'),
    name: 'nexceo_workforce_tasks',
    component: WorkforceTasksPage,
    meta: adminMeta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/ai-workforce/analytics'),
    name: 'nexceo_workforce_analytics',
    component: WorkforceAnalyticsPage,
    meta: adminMeta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/ai-core-health'),
    name: 'nexceo_ai_core_health',
    component: AiCoreHealthPage,
    meta: adminMeta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/company-ai'),
    name: 'nexceo_company_ai',
    component: CompanyAiPage,
    meta: adminMeta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/settings'),
    name: 'nexceo_settings',
    component: NexceoSettings,
    meta: adminMeta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/runtime-inspector'),
    name: 'nexceo_runtime_inspector',
    component: RuntimeInspectorPage,
    meta: adminMeta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/reports'),
    name: 'nexceo_reports',
    component: NexceoReportsPage,
    meta: adminMeta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/staff-ai'),
    name: 'nexceo_staff_ai',
    component: StaffAIPage,
    meta,
  },
  {
    path: frontendURL('accounts/:accountId/nexceo/lead-generation'),
    name: 'nexceo_lead_generation',
    component: LeadGenerationPage,
    meta: adminMeta,
  },
];
